import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Box, Button, TextField, Typography } from "@mui/material";
import {
  MY_HR_SEND_EMAIL_2FA_CODE,
  MY_HR_VERIFY_2FA_EMAIL_CODE,
} from "my-hr/graphql";
import { useMyHrUserByEmployee } from "my-hr/hooks";
import useNotification from "my-time/utils/use-notification";
import { useOcCurrentUserState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type MyHrLoginTwoFactorVerifyEmailCodeProps = {
  onForward: (userData: any) => void;
  userData: any;
  onCancel: () => void;
  isRegister?: boolean;
};

export const MyHrLoginTwoFactorVerifyEmailCode = ({
  onForward,
  userData,
  onCancel,
  isRegister,
}: MyHrLoginTwoFactorVerifyEmailCodeProps) => {
  const { t, i18n } = useTranslation();
  const { emailAddress, employeeCode } = useOcCurrentUserState();
  const { refetch: refetchUserData } = useMyHrUserByEmployee({
    employee: employeeCode,
  });

  const setOpenMessage = useNotification();
  const isMobile = useOcIsMobile();
  const previousHiddenRef = useRef(document.hidden);

  const [code, setCode] = useState("");
  const [helperText, setHelperText] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [tempCodeAt, setTempCodeAt] = useState(
    userData?.currentUser?.twoFactorTempCodeAt
      ? new Date(userData.currentUser.twoFactorTempCodeAt).getTime()
      : null,
  );

  useEffect(() => {
    if (!tempCodeAt) return;

    const calcCountdown = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - tempCodeAt) / 1000);
      const remaining = Math.max(90 - elapsed, 0);
      return remaining;
    };

    setCountdown(calcCountdown());

    const timer = setInterval(() => {
      setCountdown(() => {
        const newRemaining = calcCountdown();
        if (newRemaining <= 0) {
          clearInterval(timer);
          return 0;
        }
        return newRemaining;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [tempCodeAt]);

  // Track document visibility changes - MOBILE RELOAD FIX
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasHidden = previousHiddenRef.current;
      const isNowHidden = document.hidden;

      // MOBILE FIX: If returning to tab (hidden: true → false), reload page
      // This fixes the Chrome bug where document.hidden gets stuck at true
      if (isMobile && wasHidden && !isNowHidden) {
        window.location.reload();
        return;
      }

      previousHiddenRef.current = isNowHidden;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isMobile]);

  // Polling - MOBILE STUCK document.hidden DETECTION
  useEffect(() => {
    if (!isMobile) return;

    let lastUserInteraction = Date.now();

    // Track user interaction
    const handleInteraction = () => {
      lastUserInteraction = Date.now();
    };

    document.addEventListener("click", handleInteraction);
    document.addEventListener("input", handleInteraction);

    // Polling - checks every 2 seconds
    const intervalId = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastUserInteraction;

      // If user interacted within 5 seconds AND document.hidden is stuck
      if (timeSinceInteraction < 5000 && document.hidden) {
        clearInterval(intervalId);
        window.location.reload();
      }
    }, 2000);

    // Cleanup: Stop polling when component unmounts
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("input", handleInteraction);
    };
  }, [isMobile]);

  const [sendEmail2faCode] = useMutation(MY_HR_SEND_EMAIL_2FA_CODE, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_HR_EMAIL_VERIFICATION_CODE_SENT"),
      });
      setHelperText("");
      setTempCodeAt(Date.now());
    },
    onError: (error) => {
      setOpenMessage({
        type: "error",
        message: error?.graphQLErrors[0]?.message,
      });
    },
  });

  const [verify2faEmailCode] = useMutation(MY_HR_VERIFY_2FA_EMAIL_CODE, {
    onCompleted: () => {
      onForward(userData);
    },
    onError: async (error) => {
      // Refetch AFTER failed verification to get updated failedCode counter
      const { data: refreshedData } = await refetchUserData();
      const failedCode = refreshedData?.userByEmployee?.twoFactorFailedCode ?? 0;

      if (failedCode >= 2) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }

      let errors = error?.graphQLErrors;
      errors.map((err) => {
        setHelperText(err?.message);
        return {
          message: err?.message,
          code: err?.extensions?.code,
        };
      });
    },
  });

  const handleSendEmail = () => {
    sendEmail2faCode({
      variables: {
        email: emailAddress,
        locale: i18n.language,
      },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value;
    setCode(newCode);
  };

  const handleVerify = async () => {
    // CRITICAL FIX: Wait for tab to become visible before sending request
    // Chrome aborts fetch requests when tab is hidden!
    if (document.hidden) {
      await new Promise<void>((resolve) => {
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            resolve();
          }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Safety timeout - if user doesn't return within 30 seconds, try anyway
        setTimeout(() => {
          document.removeEventListener("visibilitychange", handleVisibilityChange);
          resolve();
        }, 30000);
      });
    }

    verify2faEmailCode({
      variables: {
        code: code,
      },
    });
  };

  return (
    <Box>
      <Button
        id="sendAuthenticationEmailButton"
        variant="contained"
        color="primary"
        sx={{ marginTop: "16px" }}
        onClick={handleSendEmail}
        fullWidth
        disabled={countdown > 0}
      >
        {t("MY_HR_SEND_AUTHENTICATION_EMAIL")}
      </Button>
      <Box sx={{ marginTop: "36px", marginBottom: "24px" }}>
        <TextField
          id="code"
          label={t("MY_HR_ENTER_AUTHENTICATION_CODE")}
          variant="outlined"
          fullWidth
          value={code}
          onChange={handleChange}
          error={!!helperText}
          helperText={helperText ? t(helperText) : " "}
          onFocus={() => setHelperText("")}
          disabled={disabled}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleVerify();
            }
          }}
        />
      </Box>
      {countdown > 0 && (
        <>
          <Box height="12px" />
          <Typography
            variant="body1"
            sx={{ textAlign: "left", marginBottom: "20px" }}
          >
            {t("MY_HR_SECONDS_REMAINING", { countdown })}
          </Typography>
        </>
      )}
      <Button
        id="verification"
        variant="contained"
        color="primary"
        sx={{ marginTop: "16px" }}
        onClick={handleVerify}
        fullWidth
        disabled={!code}
      >
        {t("MY_HR_VERIFICATION")}
      </Button>
      {!isRegister && (
        <Button
          id="cancel"
          variant="text"
          color="secondary"
          fullWidth
          sx={{ marginTop: "32px" }}
          onClick={onCancel}
        >
          {t("OC_CANCEL")}
        </Button>
      )}
    </Box>
  );
};

export default MyHrLoginTwoFactorVerifyEmailCode;
